// betaOptIn.js
//
// The beta is a cookie. `dbio_beta_opt_in=true` on `.departureboard.io` is what
// the origin override worker at the edge routes on: every request carrying it
// goes to the new site, everything else comes here. Nothing about the routing
// decision is on this side, which is why it survives being signed out and why it
// can be undone from either site.
//
// Who is *offered* the beta is a different question, and that one is a field on
// the user's Firestore document: `betaEligible`, set by the admin console. The
// profile page reads it to decide whether to show the opt-in at all. Eligibility
// is a fact about an account, the cookie is a decision about a browser, and they
// are deliberately stored in different places.

const COOKIE_NAME = "dbio_beta_opt_in";
const COOKIE_VALUE = "true";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year
const COOKIE_DOMAIN = "departureboard.io";

// The cookie this replaced. Nothing reads it any more, so it is expired on sight
// rather than left to be sent up with every request for another year.
const LEGACY_COOKIE_NAME = "dbio_uid";

// Only scope the cookie to .departureboard.io when we're actually served from
// it. On localhost a Domain attribute would cause the browser to drop the
// cookie, so leave it off and let it default to the current host.
const domainAttribute = () => {
  const { hostname } = window.location;

  if (hostname === COOKIE_DOMAIN || hostname.endsWith(`.${COOKIE_DOMAIN}`)) {
    return `; Domain=.${COOKIE_DOMAIN}`;
  }

  return "";
};

const secureAttribute = () => {
  return window.location.protocol === "https:" ? "; Secure" : "";
};

const readCookie = (name) => {
  const prefix = `${name}=`;

  for (const pair of document.cookie.split(";")) {
    const trimmed = pair.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }

  return null;
};

const expire = (name) => {
  // Expire against both the scoped and host only variants, otherwise a cookie
  // set on a different host can linger and keep routing them away.
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax${domainAttribute()}${secureAttribute()}`;
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax${secureAttribute()}`;
};

// Whether this browser has taken the offer. The same question the edge asks, and
// asked the same way, so the two can't disagree about what counts: an exact
// match, not merely a cookie that exists.
export const hasBetaOptIn = () => {
  try {
    return readCookie(COOKIE_NAME) === COOKIE_VALUE;
  } catch (error) {
    console.error("Error reading the beta opt in cookie:", error);
    return false;
  }
};

export const setBetaOptIn = () => {
  document.cookie = `${COOKIE_NAME}=${COOKIE_VALUE}; Path=/; Max-Age=${COOKIE_MAX_AGE}; SameSite=Lax${domainAttribute()}${secureAttribute()}`;
};

export const clearBetaOptIn = () => {
  expire(COOKIE_NAME);
};

// One-time tidy up of the cookie the edge used to route on. Harmless if it was
// never set.
export const clearLegacyRoutingCookie = () => {
  expire(LEGACY_COOKIE_NAME);
};
